export class Context {
  readonly stack = new DisposableStack()
  private parent?: Context
  private store: Map<any, any> = new Map()

  getChildContext() {
    const ctx = new Context()
    this.stack.use(ctx)
    ctx.parent = this
    return ctx
  }

  getParentContext() {
    return this.parent
  }

  getRootContext() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    let ctx: Context = this
    while (true) {
      if (!ctx.parent) return ctx
      ctx = ctx.parent
    }
  }

  get<T>(symbol: symbol): T | undefined {
    return this.store.get(symbol)
  }

  getEnsure<T>(symbol: symbol): T {
    const val = this.get<T>(symbol)
    if (typeof val === 'undefined') {
      throw new Error(`Context: ${String(symbol)} not found`)
    }
    return val
  }

  lookup<T>(symbol: symbol): T | undefined {
    return this.store.get(symbol) ?? this.parent?.lookup(symbol)
  }

  lookupEnsure<T>(symbol: symbol): T {
    const val = this.lookup<T>(symbol)
    if (typeof val === 'undefined') {
      throw new Error(`Context: ${String(symbol)} not found`)
    }
    return val
  }

  tree<T>(symbol: symbol): T[] {
    const vals: T[] = []
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    let p: Context = this
    while (true) {
      const val = p.store.get(symbol)
      if (typeof val !== 'undefined') vals.push(val)
      if (!p.parent) break
      p = p.parent!
    }
    return vals
  }

  getOrInsert<T>(symbol: symbol, factory: () => T) {
    if (this.store.has(symbol)) {
      return this.store.get(symbol) as T
    }
    const v = factory()
    this.set(symbol, v)
    return v
  }

  set<V>(symbol: symbol, v: V) {
    this.store.set(symbol, v)
    if (isDisposable(v)) {
      this.stack.use(v)
    }
  }

  setDisposable<V extends Disposable>(symbol: symbol, v: V) {
    this.store.set(symbol, v)
    this.stack.use(v)
  }

  unset(symbol: symbol) {
    this.store.delete(symbol)
  }

  [Symbol.dispose]() {
    this.store.clear()
    if (!this.stack.disposed) this.stack.dispose()
  }
}

const isDisposable = (obj: any): obj is Disposable => {
  return typeof obj?.[Symbol.dispose] === 'function'
}
